<script setup>

import {computed, onMounted, ref} from "vue";

const manualHoursPerWeek = ref(10);
const hourlyCost = ref(50);
const currentLeadsPerMonth = ref(5);
const leadhunterEmailsPerDay = ref(20);
const responseRate = ref(5);
const conversionRate = ref(20);
const averageDealValue = ref(5000);

const transitioned = ref(false)
const calculating = ref(false);
const seeResult = ref(false);

const results = computed(() => {
    // Time saved per month (assuming 4.33 weeks per month)
    const timeSavedPerMonth = manualHoursPerWeek.value * 4.33;

    // Cost savings from automation
    const costSavings = timeSavedPerMonth * hourlyCost.value;

    // LeadHunter performance
    const emailsPerMonth = leadhunterEmailsPerDay.value * 30;
    const responses = emailsPerMonth * (responseRate.value / 100);
    const leadhunterLeads = responses * (conversionRate.value / 100);

    // Additional leads generated
    const additionalLeads = Math.max(0, leadhunterLeads - currentLeadsPerMonth.value);

    // Additional revenue from LeadHunter
    const additionalRevenue = additionalLeads * averageDealValue.value;

    // ROI percentage (assuming LeadHunter cost is included in cost savings)
    // ROI = (Net Profit / Cost) * 100
    // Net Profit = Additional Revenue - Cost Savings (negative means we're saving)
    const roi = costSavings > 0 ? ((additionalRevenue + costSavings) / costSavings) * 100 : 0;

    // Break-even timeline (months) - when additional revenue covers the time investment
    // Assuming we need to cover the cost of manual hours
    const breakEvenMonths = additionalRevenue > 0 ? (costSavings / additionalRevenue) : 0;

    return {
        timeSavedPerMonth: timeSavedPerMonth.toLocaleString('en-US', {maximumFractionDigits:1}).replaceAll(',', ' '),
        timeSavedPerMonthRaw: timeSavedPerMonth,
        costSavings: costSavings.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        costSavingsRaw: costSavings,
        additionalLeads: additionalLeads.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalLeadsRaw: additionalLeads,
        additionalRevenue: additionalRevenue.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalRevenueRaw: additionalRevenue,
        roi: roi.toFixed(1),
        breakEvenMonths: breakEvenMonths.toFixed(1),
    };
})

const calculateInitial = () => {
    calculating.value = true

    setTimeout(function(){
        seeResult.value = true;

        setTimeout(function() {
            window.scrollBy({
                top: 500,
                behavior: 'smooth'
            });
        }, 300);
    }, 300)
}

onMounted(() => {
    setTimeout(() => {
        transitioned.value = true;
    }, 100);
})

</script>

<template>
    <div>
        <card-basic
            class="transition-all duration-500"
            :class="transitioned ? 'translate-y-0 scale-100' : '-translate-y-4 opacity-0 scale-95'"
        >
            <div class="grid-cols-2 gap-8 md:grid">
                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current manual hours per week spent on prospecting
                    </p>
                    <input-number
                        max="168"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="manualHoursPerWeek"
                        :show-step-ui="true"
                        placeholder="Hours per week"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average hourly cost (salary + overhead)
                    </p>
                    <input-number
                        max="1000"
                        min="0"
                        :step="5"
                        class="mx-auto w-36"
                        v-model:content="hourlyCost"
                        :show-step-ui="true"
                        placeholder="Hourly cost"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current leads generated per month manually
                    </p>
                    <input-number
                        max="10000"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="currentLeadsPerMonth"
                        :show-step-ui="true"
                        placeholder="Leads per month"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        LeadHunter emails sent per day
                    </p>
                    <input-number
                        max="1000"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="leadhunterEmailsPerDay"
                        :show-step-ui="true"
                        placeholder="Emails per day"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Response rate to LeadHunter campaigns (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="responseRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="responseRate = $event"
                        />{{ responseRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Conversion rate from responses to customers (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="conversionRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="conversionRate = $event"
                        />{{ conversionRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average deal value
                    </p>
                    <input-number
                        max="1000000"
                        min="0"
                        :step="100"
                        class="mx-auto w-36"
                        v-model:content="averageDealValue"
                        :show-step-ui="true"
                        placeholder="Deal value"
                    />
                </div>

                <div
                    v-if="! seeResult"
                    class="flex justify-center col-span-2"
                >
                    <button-primary
                        class="justify-center w-48 mx-auto transition-all"
                        :class="calculating ? 'translate-y-8 opacity-0' : ''"
                        @click="calculateInitial"
                    >
                        Calculate
                    </button-primary>
                </div>

                <div
                    v-if="calculating"
                    class="col-span-2 -mx-4 -mt-6 -mb-4 transition-colors duration-1000 text-wlPrimaryContrast md:-mx-6 md:-mb-6 lg:-mx-8 lg:-mb-8"
                    :class="seeResult ? 'bg-wlPrimary' : 'bg-transparent'"
                >
                    <div class="w-full h-12 transform -translate-y-px bg-white rounded-b-wl"></div>
                    <div
                        class="col-span-2 p-4 pt-3 transition-all duration-1000 rounded-b-wl md:p-6 md:pt-4 lg:p-8 lg:pt-5"
                        :class="seeResult ? 'translate-y-0 opacity-100 max-h-screen' : '-translate-y-8 opacity-0 max-h-0'"
                    >
                        <div class="flex mb-8 space-x-4">
                            <div>
                                <i class="mt-1 text-2xl fa-regular fa-chart-line"></i>
                            </div>
                            <div>
                                <p>By automating prospecting with LeadHunter, you would save <strong>{{ results.timeSavedPerMonth }} hours per month</strong>.</p>

                                <p>This translates to <strong>${{ results.costSavings }}</strong> in cost savings from automation.</p>

                                <p>LeadHunter would generate <strong>{{ results.additionalLeads }} additional leads per month</strong> compared to your current manual process.</p>

                                <p>These additional leads would generate <strong>${{ results.additionalRevenue }}</strong> in additional revenue per month.</p>

                                <p class="mt-4">
                                    <strong>ROI: {{ results.roi }}%</strong>
                                </p>

                                <p class="mt-2">
                                    <strong>Break-even timeline: {{ results.breakEvenMonths }} months</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-basic>
    </div>
</template>

<style scoped>

</style>

